import { api } from './api';
import { OrderData } from '../components/order/OrderData';

export interface OrderStepData {
  currentStep: number;
  orderId?: number;
  orderNumber?: string;
  sessionData?: Partial<OrderData>;
}

export interface OrderRecoveryData {
  orderData: Partial<OrderData>;
  currentStep: number;
  canRecover: boolean;
  error?: string;
}

class OrderPersistenceService {
  private readonly SESSION_KEY = 'nlfb_order_session';
  private readonly SESSION_EXPIRY_HOURS = 2; // 2 hours session expiry

  /**
   * Save current step and order data to session storage
   */
  saveToSession(stepData: OrderStepData): void {
    const sessionData = {
      ...stepData,
      timestamp: Date.now(),
      expiresAt: Date.now() + (this.SESSION_EXPIRY_HOURS * 60 * 60 * 1000)
    };
    
    try {
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
      console.log('OrderPersistence: Saved to session', sessionData);
    } catch (error) {
      console.warn('OrderPersistence: Failed to save to session', error);
    }
  }

  /**
   * Load order data from session storage
   */
  loadFromSession(): OrderStepData | null {
    try {
      const sessionData = sessionStorage.getItem(this.SESSION_KEY);
      if (!sessionData) return null;

      const parsed = JSON.parse(sessionData);
      
      // Check if session has expired
      if (Date.now() > parsed.expiresAt) {
        this.clearSession();
        return null;
      }

      console.log('OrderPersistence: Loaded from session', parsed);
      return parsed;
    } catch (error) {
      console.warn('OrderPersistence: Failed to load from session', error);
      this.clearSession();
      return null;
    }
  }

  /**
   * Clear session storage
   */
  clearSession(): void {
    try {
      sessionStorage.removeItem(this.SESSION_KEY);
      // Also clear any other potential session keys
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.includes('nlfb')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      console.log('OrderPersistence: Cleared session and related keys', keysToRemove);
    } catch (error) {
      console.warn('OrderPersistence: Failed to clear session', error);
    }
  }

  /**
   * Detect current step based on database data
   */
  async detectCurrentStep(orderId: number): Promise<number> {
    try {
      const response = await api.get(`/v1/orders/${orderId}`);
      // API returns { data: order }; support both shapes for robustness
      const order = response.data.data ?? response.data.order;
      if (!order) {
        console.warn('OrderPersistence: No order in response', { keys: Object.keys(response.data || {}) });
        return 1;
      }

      const hasCustomerDetails = !!(order.customer_details ?? order.customerDetails);
      const hasConfiguration = !!order.configuration;
      const isSubmitted = order.is_submitted === true || order.status === 'processing';

      console.log('OrderPersistence: Detecting step for order', {
        orderId,
        hasCustomerDetails,
        hasConfiguration,
        isSubmitted
      });

      // Step detection logic based on what data exists
      if (!hasCustomerDetails) {
        console.log('OrderPersistence: No customer details, returning step 1');
        return 1; // Customer details step
      }
      
      if (!hasConfiguration) {
        console.log('OrderPersistence: No configuration, returning step 2');
        return 2; // Configuration step
      }
      
      if (!isSubmitted) {
        console.log('OrderPersistence: Not submitted, returning step 3');
        return 3; // Checkout step
      }
      
      console.log('OrderPersistence: Order submitted, returning step 4');
      return 4; // Confirmation step
    } catch (error) {
      console.error('OrderPersistence: Failed to detect step from database', error);
      throw new Error('Unable to load order data');
    }
  }

  /**
   * Load order data from database
   */
  async loadOrderFromDatabase(orderId: number): Promise<Partial<OrderData>> {
    try {
      const response = await api.get(`/v1/orders/${orderId}`);
      // API returns { data: order }; support both shapes for robustness
      const order = response.data.data ?? response.data.order;
      if (!order) {
        throw new Error('No order in API response');
      }

      const cd = order.customer_details ?? order.customerDetails;
      const config = order.configuration;

      // Transform database data to OrderData format
      const orderData: Partial<OrderData> = {
        orderId: order.id,
        orderNumber: order.order_number,
        assignedParcelDay: order.parcel_day ? {
          id: order.parcel_day.id,
          date: order.parcel_day.date,
          weekday: order.parcel_day.weekday
        } : null,
        customerDetails: cd ? {
          is_first_time: cd.is_first_time,
          adults_count: cd.adults_count,
          children_count: cd.children_count,
          firstName: cd.first_name,
          lastName: cd.last_name,
          email: cd.email,
          phone: cd.phone,
          delivery_method: cd.delivery_method || 'pickup',
          address: cd.address_line1,
          suburb: cd.suburb,
          postcode: cd.postcode,
          notes: cd.notes || ''
        } : null,
        orderConfiguration: config ? {
          configuration: {
            bundle_selections: [
              config.bundle_selection_1,
              config.bundle_selection_2,
              config.bundle_selection_3
            ],
            bread_selection: config.bread_selection,
            grocery_selections: [
              config.grocery_selection_1,
              config.grocery_selection_2
            ],
            parcel_item_selections: config.parcel_item_selections || {},
            addon_product_selections: config.addon_product_selections || {}
          },
          totalCalculation: {
            total: order.total_amount,
            breakdown: {} // This would need to be calculated from order items
          }
        } : null
      };

      // Load food parcel data if available
      if (order.food_parcel) {
        orderData.foodParcel = {
          id: order.food_parcel.id,
          name: order.food_parcel.name,
          price: order.food_parcel.price
        };
      }

      console.log('OrderPersistence: Loaded order from database', {
        orderId: orderData.orderId,
        hasCustomerDetails: !!orderData.customerDetails,
        hasOrderConfiguration: !!orderData.orderConfiguration,
        customerDetails: orderData.customerDetails
      });
      return orderData;
    } catch (error) {
      console.error('OrderPersistence: Failed to load order from database', error);
      throw new Error('Unable to load order data');
    }
  }

  /**
   * Attempt to recover order state from session and database
   */
  async recoverOrderState(): Promise<OrderRecoveryData> {
    try {
      // First, try to load from session
      const sessionData = this.loadFromSession();
      
      if (sessionData?.orderId) {
        try {
          // Load order data from database
          const orderData = await this.loadOrderFromDatabase(sessionData.orderId);
          const currentStep = await this.detectCurrentStep(sessionData.orderId);
          
          // Check if session data is stale (has null customer details but database has them)
          const sessionHasStaleData = !sessionData.sessionData?.customerDetails && orderData.customerDetails;
          
          if (sessionHasStaleData) {
            console.log('OrderPersistence: Detected stale session data, clearing session and using database data');
            this.clearSession();
            
            // Use database data as the primary source
            const freshOrderData = {
              ...orderData,
              // Preserve session-specific data that might not be in database
              foodParcel: sessionData.sessionData?.foodParcel || orderData.foodParcel,
              assignedParcelDay: sessionData.sessionData?.assignedParcelDay || orderData.assignedParcelDay,
            };
            
            console.log('OrderPersistence: Using fresh data from database', {
              hasCustomerDetails: !!freshOrderData.customerDetails,
              currentStep
            });
            
            return {
              orderData: freshOrderData,
              currentStep,
              canRecover: true
            };
          }
          
          // Merge session data with database data, prioritizing database data
          const mergedOrderData = {
            ...sessionData.sessionData, // Start with session data
            ...orderData, // Override with database data (this ensures customer details from DB are used)
            // Preserve session-specific data that might not be in database
            foodParcel: sessionData.sessionData?.foodParcel || orderData.foodParcel,
            assignedParcelDay: sessionData.sessionData?.assignedParcelDay || orderData.assignedParcelDay,
          };
          
          console.log('OrderPersistence: Merged order data', {
            sessionCustomerDetails: !!sessionData.sessionData?.customerDetails,
            databaseCustomerDetails: !!orderData.customerDetails,
            mergedCustomerDetails: !!mergedOrderData.customerDetails,
            currentStep
          });
          
          return {
            orderData: mergedOrderData,
            currentStep,
            canRecover: true
          };
        } catch (error) {
          console.warn('OrderPersistence: Failed to load from database, clearing session', error);
          this.clearSession();
        }
      }

      // If no session or database load failed, start fresh
      return {
        orderData: {},
        currentStep: 0,
        canRecover: false
      };
    } catch (error) {
      console.error('OrderPersistence: Recovery failed', error);
      return {
        orderData: {},
        currentStep: 0,
        canRecover: false,
        error: 'Failed to recover order state'
      };
    }
  }

  /**
   * Update session with new order data
   */
  updateSessionOrderData(orderData: Partial<OrderData>): void {
    const sessionData = this.loadFromSession();
    if (sessionData) {
      this.saveToSession({
        ...sessionData,
        sessionData: {
          ...sessionData.sessionData,
          ...orderData
        }
      });
    }
  }

  /**
   * Check if session is valid and not expired
   */
  isSessionValid(): boolean {
    const sessionData = this.loadFromSession();
    return sessionData !== null;
  }
}

export const orderPersistenceService = new OrderPersistenceService();
